/**
 * 사업자 정보 — 순수 부분(타입·라벨·정리·검사·표시 줄). 클라이언트 번들에서도 안전(서버 의존 없음).
 * 서버 로더는 lib/company-info.ts(getCompanyInfo).
 */

export const COMPANY_NAMESPACE = 'company';
export const COMPANY_CACHE_TAG = 'app-config-company';

export const COMPANY_KEYS = ['biz_name', 'ceo', 'biz_no', 'ecommerce_no', 'address', 'rep_tel', 'email'] as const;
export type CompanyKey = (typeof COMPANY_KEYS)[number];
export type CompanyInfo = Record<CompanyKey, string>;

export const COMPANY_LABELS: Record<CompanyKey, string> = {
  biz_name: '상호',
  ceo: '대표자',
  biz_no: '사업자등록번호',
  ecommerce_no: '통신판매업 신고번호',
  address: '주소',
  rep_tel: '대표번호',
  email: '이메일',
};

/** 견적서 등 사이트 밖으로 나가는 문서의 공급자 칸 — 카더라 사업 정보만(대표자 이름·주소 제외, 운영자 지시 2026-10-30).
 *  대표자·주소는 법정 표시(전자상거래법 제10조)인 사이트 하단 «사업자 정보»(접힘)에만 둔다. */
export const EXTERNAL_DOC_KEYS: readonly CompanyKey[] = ['biz_name', 'biz_no', 'ecommerce_no', 'rep_tel', 'email'];

export const EMPTY_COMPANY: CompanyInfo = { biz_name: '', ceo: '', biz_no: '', ecommerce_no: '', address: '', rep_tel: '', email: '' };

export const COMPANY_MAX_LEN = 200;

/** 전화번호 모양(010-1234-5678 · 02-123-4567 · 1588-1234 · 0511234567). 사업자번호(000-00-00000)는 해당 없음. */
const PHONE_RE = /(?:^|[^\d])(?:0\d{1,2}[-.\s]?\d{3,4}[-.\s]?\d{4}|1[5-9]\d{2}[-.\s]?\d{4})(?!\d)/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** 대표번호(전국대표번호 15xx·16xx·18xx 등)만 — 휴대폰·일반 유선번호(개인 번호)는 받지 않는다(운영자 지시 2026-10-30: 카더라 대표번호 1833-7942). */
const REP_TEL_RE = /^1[5-9]\d{2}-?\d{4}$/;

export function isRepTel(v: string): boolean {
  return REP_TEL_RE.test(v.trim());
}

/** 카더라 대표번호(회사 번호 · 운영자 지시 10/30) — 관리자 입력(rep_tel)이 비었을 때 광고 안내·신청 화면의 상담 번호로 쓴다. 개인 번호 아님. */
export const KADEORA_REP_TEL = '1833-7942';

/** 광고 상담 번호 — 관리자 입력 대표번호(형식 맞을 때) → 없으면 카더라 대표번호. 휴대폰·일반 유선번호는 절대 나오지 않는다. */
export function adsContactTel(info: Pick<CompanyInfo, 'rep_tel'> | null | undefined): string {
  const v = String(info?.rep_tel ?? '').trim();
  return v && isRepTel(v) ? v : KADEORA_REP_TEL;
}

/** 전화번호 모양 검사에서 빼는 키 — 번호 자체가 값인 칸. */
const PHONE_EXEMPT: readonly CompanyKey[] = ['biz_no', 'ecommerce_no', 'rep_tel'];

export function looksLikePhone(v: string): boolean {
  return PHONE_RE.test(v);
}

/** 한 항목 정리 — 문자열이 아니면 빈 값, 공백 정리, 길이 제한. */
function clean(v: unknown): string {
  if (typeof v !== 'string') return '';
  return v.replace(/\s+/g, ' ').trim().slice(0, COMPANY_MAX_LEN);
}

/** app_config 행 값(무엇이든) → CompanyInfo. 모르는 키는 버린다. */
export function normalizeCompanyInfo(raw: Partial<Record<string, unknown>> | null | undefined): CompanyInfo {
  const out: CompanyInfo = { ...EMPTY_COMPANY };
  if (!raw) return out;
  for (const k of COMPANY_KEYS) out[k] = clean(raw[k]);
  return out;
}

/** 관리자 입력 검사 — 문제 있는 키와 사유. 빈 값은 언제나 허용(그 항목 숨김). */
export function validateCompanyInfo(info: CompanyInfo): { key: CompanyKey; reason: string }[] {
  const errs: { key: CompanyKey; reason: string }[] = [];
  for (const k of COMPANY_KEYS) {
    const v = info[k];
    if (!v) continue;
    if (!PHONE_EXEMPT.includes(k) && looksLikePhone(v)) errs.push({ key: k, reason: '전화번호는 넣을 수 없습니다' });
  }
  if (info.rep_tel && !isRepTel(info.rep_tel)) errs.push({ key: 'rep_tel', reason: '대표번호(예: 1833-7942)만 넣을 수 있습니다' });
  if (info.email && !EMAIL_RE.test(info.email)) errs.push({ key: 'email', reason: '이메일 형식이 아닙니다' });
  if (info.biz_no && !/^[\d-]{10,12}$/.test(info.biz_no)) errs.push({ key: 'biz_no', reason: '숫자와 - 만 (예: 123-45-67890)' });
  return errs;
}

/** 표시할 줄 — 빈 값·전화번호 모양(방어)은 뺀다. 전부 비면 []. */
export function companyInfoRows(info: CompanyInfo | null | undefined): { key: CompanyKey; label: string; value: string }[] {
  if (!info) return [];
  const rows: { key: CompanyKey; label: string; value: string }[] = [];
  for (const k of COMPANY_KEYS) {
    const v = clean(info[k]);
    if (!v) continue;
    if (!PHONE_EXEMPT.includes(k) && looksLikePhone(v)) continue;
    if (k === 'rep_tel' && !isRepTel(v)) continue;
    if (k === 'email' && !EMAIL_RE.test(v)) continue;
    rows.push({ key: k, label: COMPANY_LABELS[k], value: v });
  }
  return rows;
}

